import { useState, useEffect, useCallback, useRef } from "react";

// Zoom/pan state + wheel/resize handling for the SVG canvas
export function useCanvasView(imageDims) {
  const svgRef = useRef(null);
  const wrapRef = useRef(null);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [wrapSize, setWrapSize] = useState({ w: 800, h: 600 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWrapSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setWrapSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // Fit image to view when image or wrapper size changes
  useEffect(() => {
    if (!imageDims || !wrapSize.w) return;
    const pad = 60;
    const scale = Math.min((wrapSize.w - pad * 2) / imageDims.width, (wrapSize.h - pad * 2) / imageDims.height, 1.5);
    setView({
      scale,
      tx: (wrapSize.w - imageDims.width * scale) / 2,
      ty: (wrapSize.h - imageDims.height * scale) / 2,
    });
  }, [imageDims, wrapSize]);

  const clientToImage = useCallback((clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left - view.tx) / view.scale, y: (clientY - rect.top - view.ty) / view.scale };
  }, [view]);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = -e.deltaY > 0 ? 1.12 : 1 / 1.12;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setView((v) => {
        const newScale = Math.max(0.1, Math.min(8, v.scale * factor));
        const ratio = newScale / v.scale;
        return { scale: newScale, tx: cx - (cx - v.tx) * ratio, ty: cy - (cy - v.ty) * ratio };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const zoomBy = useCallback((f) => setView((v) => ({ ...v, scale: Math.max(0.1, Math.min(8, v.scale * f)) })), []);
  const panBy = useCallback((tx, ty) => setView((v) => ({ ...v, tx, ty })), []);

  return { svgRef, wrapRef, view, clientToImage, zoomBy, panBy };
}
